import { ILeisureProductsByIdsRequestParams } from '@/models/Leisure/interfaces/ILeisureProductsByIdsRequestParams';
import LeisureBrandDetailJSC from '@/schema/LeisureBrandDetailJSC';
import LeisureBrandListJSC from '@/schema/LeisureBrandListJSC';
import LeisureBrandTicketsJSC from '@/schema/LeisureBrandTicketsJSC';
import LeisureProductsByIdsJSC from '@/schema/LeisureProductsByIdsJSC';
import LeisureTicketListJSC from '@/schema/LeisureTicketListJSC';
import { Either } from 'tsmonad';

import LeisureAreasJSC from '../../schema/LeisureAreasJSC';
import LeisureBrandListByCategoryJSC from '../../schema/LeisureBrandListByCategoryJSC';
import LeisureCategoriesJSC from '../../schema/LeisureCategoriesJSC';
import LeisureCrossSellListJSC from '../../schema/LeisureCrossSellListJSC';
import LeisureCrossSellWidgetJSC from '../../schema/LeisureCrossSellWidgetJSC';
import LeisureHotDealListJSC from '../../schema/LeisureHotDealListJSC';
import LeisureMediumCategoriesJSC from '../../schema/LeisureMediumCategoriesJSC';
import LeisureNearbyJSC from '../../schema/LeisureNearbyJSC';
import LeisureRecentsJSC from '../../schema/LeisureRecentsJSC';
import LeisureRecommendJSC from '../../schema/LeisureRecommendJSC';
import LeisureSpecialEventDetailJSC from '../../schema/LeisureSpecialEventDetailJSC';
import { TReqConfigValidated } from '../../services/JSCService';
import { SunnyRequestBuilder } from '../Common/SunnyRequestBuilder';
import { ILeisureAreasRequestParams } from './interfaces/ILeisureAreasRequestParams';
import { ILeisureBrandDetailRequestParams } from './interfaces/ILeisureBrandDetailRequestParams';
import { ILeisureBrandListByCategoryRequestParams } from './interfaces/ILeisureBrandListByCategoryRequestParams';
import { ILeisureBrandListRequestParams } from './interfaces/ILeisureBrandListRequestParams';
import { ILeisureBrandTicketsRequestParams } from './interfaces/ILeisureBrandTicketsRequestParams';
import { ILeisureCategoriesRequestParams } from './interfaces/ILeisureCategoriesRequestParams';
import { ILeisureCrossSellListRequestParams } from './interfaces/ILeisureCrossSellListRequestParams';
import { ILeisureCrossSellWidgetRequestParams } from './interfaces/ILeisureCrossSellWidgetRequestParams';
import { ILeisureHotDealListRequestParams } from './interfaces/ILeisureHotdealRequestParams';
import { ILeisureMediumCategoriesRequestParams } from './interfaces/ILeisureMediumCategoriesRequestParams';
import { ILeisureNearbyRequestParams } from './interfaces/ILeisureNearbyRequestParams';
import { ILeisureRecentsRequestParams } from './interfaces/ILeisureRecentsRequestParams';
import { ILeisureRecommendRequestParams } from './interfaces/ILeisureRecommendRequestParams';
import { ILeisureSpecialEventDetailRequestParams } from './interfaces/ILeisureSpecialEventDetailRequestParams';
import { ILeisureTicketListRequestParams } from './interfaces/ILeisureTicketListRequestParams';

export class LeisureRequestBuilder extends SunnyRequestBuilder {
  createGetAreasQuery(reqParams?: ILeisureAreasRequestParams): TReqConfigValidated {
    return this.validate(LeisureAreasJSC, reqParams).map((params) => this.makeConfig('GET', '/leisure/v3/areas', params));
  }

  createGetCategoriesQuery(reqParams?: ILeisureCategoriesRequestParams): TReqConfigValidated {
    return this.validate(
      reqParams?.query && Object.keys(reqParams?.query).length ? LeisureCategoriesJSC : {},
      reqParams,
    ).map((params) => this.makeConfig('GET', '/leisure/v3/categories', params));
  }

  createGetMediumCategoriesQuery(reqParams?: ILeisureMediumCategoriesRequestParams): TReqConfigValidated {
    return this.validate(LeisureMediumCategoriesJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/categories/medium', params),
    );
  }

  createGetBrandListByCategory(reqParams: ILeisureBrandListByCategoryRequestParams): TReqConfigValidated {
    return this.validate(LeisureBrandListByCategoryJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/categories/brands', params),
    );
  }

  createGetHomePopularCategoriesQuery(): TReqConfigValidated<never> {
    return Either.right(this.makeConfig('GET', '/leisure/categories/v2/popular'));
  }

  createGetHomeTitleQuery(): TReqConfigValidated<never> {
    return Either.right(this.makeConfig('GET', '/leisure/title'));
  }

  createGetHomeWidgetOrderQuery(): TReqConfigValidated<never> {
    return Either.right(this.makeConfig('GET', '/leisure/widget_order'));
  }

  createGetMDPicksQuery(): TReqConfigValidated<never> {
    return Either.right(this.makeConfig('GET', '/leisure/md_pick'));
  }

  createGetNearbyQuery(reqParams: ILeisureNearbyRequestParams): TReqConfigValidated {
    return this.validate(LeisureNearbyJSC, reqParams).map((params) => this.makeConfig('GET', '/leisure/v3/near', params));
  }

  createGetNearbyHotdealsQuery(reqParams: ILeisureNearbyRequestParams): TReqConfigValidated {
    return this.validate(LeisureNearbyJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/near_hotdeal', params),
    );
  }

  createGetPopularRecommendations(): TReqConfigValidated<never> {
    return Either.right({
      method: 'POST',
      headers: { ...this.AccessTokenObject },
      timeout: 5000,
      url: this.getAPIPath('/leisure/recommends/popular').href(),
    });
  }

  createGetRecentsQuery(reqParams?: ILeisureRecentsRequestParams): TReqConfigValidated {
    return this.validate(LeisureRecentsJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/recently_visited', params),
    );
  }

  createGetSpecialEventListQuery(): TReqConfigValidated<never> {
    return Either.right(this.makeConfig('GET', '/leisure/special_event'));
  }

  createGetSpecialEventDetailQuery(reqParams: ILeisureSpecialEventDetailRequestParams): TReqConfigValidated {
    return this.validate(LeisureSpecialEventDetailJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/leisure/special_event/${params.resources.event_id}`, params),
    );
  }

  createGetShockingHotDealQuery(reqParams: ILeisureHotDealListRequestParams): TReqConfigValidated {
    return this.validate(LeisureHotDealListJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/special_price_list', params),
    );
  }

  createGetTicketListQuery(reqParams: ILeisureTicketListRequestParams): TReqConfigValidated {
    return this.validate(LeisureTicketListJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/tickets', params),
    );
  }

  createGetProductsQuery(reqParams: ILeisureTicketListRequestParams): TReqConfigValidated {
    return this.validate(LeisureTicketListJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/products', params, undefined, (path) => {
        const baseURI = this.baseURI.href();
        return baseURI ? `${baseURI}${path}` : `${baseURI}/api/v1${path}`;
      }),
    );
  }
  /** 사용처: 마이페이지 -> 쿠폰함 -> 레저상품 디테일페이지 */
  createGetProductsByIds(reqParams: ILeisureProductsByIdsRequestParams): TReqConfigValidated {
    return this.validate(LeisureProductsByIdsJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/leisure/v3/products/ids', params),
    );
  }

  createPostRecommendListQuery(reqParams: ILeisureRecommendRequestParams): TReqConfigValidated {
    return this.validate(LeisureRecommendJSC, reqParams).map((params) =>
      this.makeConfig('POST', '/leisure/recommends', params),
    );
  }

  createGetPopularBrandListQuery(): TReqConfigValidated {
    return Either.right(this.makeConfig('GET', '/leisure/v3/brands/popular'));
  }

  createGetBrandCategoryListQuery(): TReqConfigValidated {
    return Either.right(this.makeConfig('GET', '/leisure/v3/brands/categories'));
  }

  createGetBrandListQuery(reqParams: ILeisureBrandListRequestParams): TReqConfigValidated {
    return this.validate(LeisureBrandListJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/brands', params),
    );
  }

  createGetBrandDetailQuery(reqParams: ILeisureBrandDetailRequestParams): TReqConfigValidated {
    return this.validate(LeisureBrandDetailJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/leisure/v3/brands/${params.resources.brand_id}`, params),
    );
  }

  createGetBrandTicketsQuery(reqParams: ILeisureBrandTicketsRequestParams): TReqConfigValidated {
    return this.validate(LeisureBrandTicketsJSC, reqParams).map((params) =>
      this.makeConfig('GET', `/leisure/v3/brands/${params.resources.brand_id}/tickets`, params),
    );
  }

  createGetCrossSellWidgetQuery(reqParams: ILeisureCrossSellWidgetRequestParams): TReqConfigValidated {
    return this.validate(LeisureCrossSellWidgetJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/cross_sell/region/widget', params),
    );
  }

  createGetCrossSellListQuery(reqParams: ILeisureCrossSellListRequestParams): TReqConfigValidated {
    return this.validate(LeisureCrossSellListJSC, reqParams).map((params) =>
      this.makeConfig('GET', '/leisure/v3/cross_sell/region/items', params),
    );
  }
}
